import React from "react";

// Shared base for all React Zest v2 components. Centralizes the root
// element, the data-zest-component marker, and ref forwarding — the seam
// for adding shared theme/className behaviour across the library.

type VariantValue = string | number | boolean | null | undefined;

// The props ZestComponent owns and consumes itself; everything else is
// passthrough for the rendered element.
interface OwnProps {
  name: string;
  className?: string;
  style?: React.CSSProperties;
  variants?: Record<string, VariantValue>;
  children?: React.ReactNode;
}

const variantAttrs = (variants?: Record<string, VariantValue>): Record<string, VariantValue> =>
  Object.fromEntries(
    Object.entries(variants ?? {})
      .filter(([, value]) => value != null)
      .map(([key, value]) => [`data-zest-component--${key}`, value])
  );

// Passthrough is keyed off whichever tag `as` selects, so tag-specific
// attributes type-check as well (href on an anchor, type/disabled on a
// button) — plus open-ended data-* hooks. Narrower than the
// Record<string, unknown> this used to be, where a typo in a real prop name —
// `classsName`, `arai-label` — silently became a no-op instead of a type error.
export type ZestComponentProps<T extends React.ElementType = "div"> = OwnProps & { as?: T } & Omit<
    React.ComponentPropsWithRef<T>,
    keyof OwnProps | "as"
  > &
  Record<`data-${string}`, unknown>;

// forwardRef can't carry a generic parameter through on its own, so the render
// function is typed loosely and the generic contract is reapplied by the cast
// below — the usual workaround for polymorphic `as` components.
type RenderProps = OwnProps & { as?: React.ElementType } & Record<string, unknown>;

interface PolymorphicZestComponent {
  <T extends React.ElementType = "div">(props: ZestComponentProps<T>): JSX.Element | null;
  displayName?: string;
}

const ZestComponent = React.forwardRef<Element, RenderProps>(
  ({ as: Tag = "div", name, className, style, variants, children, ...rest }, ref) => (
    // Passthrough props go first so the generated marker and variant
    // attributes stay authoritative and can't be overwritten by a caller.
    <Tag {...rest} ref={ref} data-zest-component={name} className={className} style={style} {...variantAttrs(variants)}>
      {children}
    </Tag>
  )
) as PolymorphicZestComponent;

ZestComponent.displayName = "ZestComponent";

export default ZestComponent;
